import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDocumentParams } from 'app/router/params';
import { v4 as uuidv4 } from 'uuid';

import { StyledDataSheet } from 'app/components';
import { getTagHighlightColor, getTagIndicatorColor } from 'slate/extensions/tag';
import type { TagColorResolver } from 'shared/data-display/StyledDataSheet';
import { CurrentFileBlob, XlsxFile, RangeSelection, SheetDataResult, SelectedDataResult } from './UpdateReferenceData';
import { ReviewTags, ReviewVerdict } from './ReviewTags';
import { labelAwareDiff } from './compareDiff';
import { gridWidth, styleCarryReport } from './styleCarryReport';
import { carryTagData } from './tagMarkers';
import { tableHasTags } from './updatePermissions';
import type { MatchMode, MatchTagsBody, MatchTagsResult, ReattachTarget } from './types';
import { wrapper } from './wrapper-helper';
import { useMatchTagsForward, useUpdateReferenceData } from 'entities/reference-data';
import { useTagDisplay } from 'app/state/contexts/TagContext';
import { resolveConceptLabelOrName } from 'app/utils/tag-utils';
import { ReferenceCell, ReferenceDataValue, EditorDocument } from 'entities/document';

type TaggableCell = ReferenceCell & { tags?: string[] };

const selectionExtent = (selection?: RangeSelection) =>
  selection?.start && selection?.end
    ? { rows: Math.abs(selection.end.i - selection.start.i) + 1, cols: Math.abs(selection.end.j - selection.start.j) + 1 }
    : null;

const sheetExtent = (sheet?: ReferenceCell[][]) => (sheet?.length ? { rows: sheet.length, cols: gridWidth(sheet) } : null);

const taggedCellsOf = (grid?: ReferenceCell[][]) => {
  const cells: { cellRow: number; cellCol: number; tagId: string }[] = [];
  (grid ?? []).forEach((row, cellRow) =>
    (row ?? []).forEach((cell, cellCol) => ((cell as TaggableCell)?.tags ?? []).forEach((tagId) => cells.push({ cellRow, cellCol, tagId })))
  );
  return cells;
};

const MAX_STYLE_ISSUES = 5;

const readOnlyGrid = (grid: ReferenceCell[][] | undefined): ReferenceCell[][] =>
  (grid ?? []).map((row) => (row ?? []).map((cell) => ({ ...(cell ?? {}), readOnly: true }))) as ReferenceCell[][];

const withDecisions = (grid: ReferenceCell[][] | undefined, reattached: Map<string, ReattachTarget>, dropped: Set<string>): ReferenceCell[][] | undefined => {
  if (!grid || (reattached.size === 0 && dropped.size === 0)) return grid;
  const copy = grid.map((row) =>
    (row ?? []).map((cell) => {
      const tags = (cell as TaggableCell)?.tags?.filter((tagId) => !dropped.has(tagId));
      return { ...(cell ?? {}), tags: tags?.length ? tags : undefined };
    })
  );
  reattached.forEach(({ row, col }, tagId) => {
    const cell = copy[row]?.[col];
    if (!cell) return;
    cell.tags = [...new Set([...(cell.tags ?? []), tagId])];
  });
  return copy as ReferenceCell[][];
};

interface CompareUpdateProps {
  pDocument?: EditorDocument;
  selectedData: SelectedDataResult;
  sheetData: SheetDataResult;
  currentReferenceData: ReferenceDataValue;
  currentFile?: XlsxFile;
  maybeFile?: XlsxFile;
  maybeSelection?: RangeSelection;
  maybeSheet?: string;
  onNewFileClick: () => void;
  onNewSelectionClick: () => void;
  onAbortClick: () => void;
  onSaveSuccess: () => void;
  taggerWarning?: boolean;
  notice?: ReactNode;
}

const resolveTagColors: TagColorResolver = (opt, data) => ({ highlight: getTagHighlightColor(opt, data), indicator: getTagIndicatorColor(opt, data) });

export function CompareUpdate(props: CompareUpdateProps) {
  const { t } = useTranslation(['common', 'reference-data']);

  const {
    pDocument,
    selectedData,
    sheetData,
    currentReferenceData,
    currentFile,
    maybeFile,
    maybeSelection,
    maybeSheet,
    onNewFileClick,
    onNewSelectionClick,
    onAbortClick,
    onSaveSuccess,
    taggerWarning = false,
    notice = null,
  } = props;
  const { documentId } = useDocumentParams();
  const { mutate: updateReferenceData } = useUpdateReferenceData();
  const { mutate: matchTags } = useMatchTagsForward();

  const [match, setMatch] = useState<MatchTagsResult | null>(null);
  const [matchFailed, setMatchFailed] = useState(false);
  const inFlightKey = useRef<string | null>(null);
  const [pendingTagId, setPendingTagId] = useState<string | null>(null);
  const [dropped, setDropped] = useState<Set<string>>(() => new Set());
  const [reattached, setReattached] = useState<Map<string, ReattachTarget>>(() => new Map());
  const [showAllStyleIssues, setShowAllStyleIssues] = useState(false);
  const [mode, setMode] = useState<MatchMode>('position');

  const priorHasTags = tableHasTags(currentReferenceData.selectionContent);
  const selectionKey = [
    mode,
    maybeFile?.revisionId,
    maybeSheet,
    maybeSelection?.start?.i,
    maybeSelection?.start?.j,
    maybeSelection?.end?.i,
    maybeSelection?.end?.j,
  ].join('|');

  useEffect(() => {
    setMatch(null);
    setMatchFailed(false);
    setPendingTagId(null);
    setDropped(new Set());
    setReattached(new Map());
    setShowAllStyleIssues(false);
    inFlightKey.current = selectionKey;
    if (!selectedData.isSuccess || !priorHasTags || !selectedData.data || !currentReferenceData.superId) return;
    const requestKey = selectionKey;
    matchTags(
      {
        documentSuperId: Number(documentId),
        referenceDataSuperId: currentReferenceData.superId,
        selectionContent: selectedData.data,
        mode,
      } as MatchTagsBody,
      {
        onSuccess: (result: MatchTagsResult | undefined) => {
          if (inFlightKey.current === requestKey) setMatch(result ?? null);
        },
        onError: () => {
          if (inFlightKey.current === requestKey) setMatchFailed(true);
        },
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedData.isSuccess, selectionKey]);

  const workingContent = useMemo(
    () => withDecisions((match?.selectionContent as ReferenceCell[][] | undefined) ?? selectedData.data, reattached, dropped),
    [match, selectedData.data, reattached, dropped]
  );

  const { changedNew, changedOld, matchedRows } = useMemo(
    () => labelAwareDiff(currentReferenceData.selectionContent ?? [], selectedData.data ?? []),
    [currentReferenceData.selectionContent, selectedData.data]
  );
  const styleIssues = useMemo(
    () => styleCarryReport(currentReferenceData.selectionContent ?? [], selectedData.data ?? []),
    [currentReferenceData.selectionContent, selectedData.data]
  );
  const bothHaveRows = (currentReferenceData.selectionContent?.length ?? 0) > 1 && (selectedData.data?.length ?? 0) > 1;
  const comparable = matchedRows > 0;
  const positionView = priorHasTags && !taggerWarning && mode === 'position';
  const highlightNew = comparable && !positionView ? changedNew : [];
  const highlightOld = comparable && !positionView ? changedOld : [];
  const sheet = sheetExtent(sheetData.data);
  const oldSize = sheetExtent(currentReferenceData.selectionContent);
  const newSize = sheetExtent(selectedData.data);
  const sizeNotice =
    oldSize && newSize && (oldSize.rows !== newSize.rows || oldSize.cols !== newSize.cols)
      ? { oldRows: oldSize.rows, oldCols: oldSize.cols, newRows: newSize.rows, newCols: newSize.cols }
      : null;
  const selected = selectionExtent(maybeSelection);
  const rangeNotice =
    sheet && selected && (sheet.rows > selected.rows || sheet.cols > selected.cols) ? { ...sheet, selRows: selected.rows, selCols: selected.cols } : null;
  const showTagMarkers = priorHasTags && !taggerWarning && !!match;
  const savingWouldDropTags = priorHasTags && !match;
  const lostCount = (match?.results ?? []).filter((r) => r.disposition === 'unmatched').length;
  const partialCount = (match?.results ?? []).filter((r) => r.partial).length;
  const movedCount = (match?.results.length ?? 0) - lostCount - partialCount;
  const taggedOld = useMemo(
    () => (showTagMarkers ? taggedCellsOf(currentReferenceData.selectionContent) : []),
    [showTagMarkers, currentReferenceData.selectionContent]
  );
  const taggedNew = useMemo(() => (showTagMarkers ? taggedCellsOf(workingContent) : []), [showTagMarkers, workingContent]);
  const switchMode = () => setMode((m) => (m === 'position' ? 'label' : 'position'));
  const matcherLink = <button type="button" className="button clear compare-inline-link" onClick={switchMode} />;
  const tagData = useMemo(() => carryTagData(match?.results, reattached, dropped), [match, reattached, dropped]);
  const { tagLanguage } = useTagDisplay();
  const conceptLabels = useMemo(
    () =>
      Object.fromEntries(
        (match?.results ?? []).filter((r) => r.concept).map((r) => [r.tagId, resolveConceptLabelOrName({ concept: r.concept, currentLang: tagLanguage }) ?? ''])
      ),
    [match, tagLanguage]
  );
  const displayOld = useMemo(() => readOnlyGrid(currentReferenceData.selectionContent), [currentReferenceData.selectionContent]);
  const displayNew = useMemo(() => readOnlyGrid(workingContent), [workingContent]);

  const handleNewGridSelect = (selection: RangeSelection) => {
    if (!pendingTagId || !selection?.start) return;
    const { i: row, j: col } = selection.start;
    setReattached((prev) => new Map(prev).set(pendingTagId, { row, col }));
    setPendingTagId(null);
  };

  const handleDrop = (tagId: string) => setDropped((prev) => new Set(prev).add(tagId));

  const handleUndo = (tagId: string) => {
    setDropped((prev) => {
      const next = new Set(prev);
      next.delete(tagId);
      return next;
    });
    setReattached((prev) => {
      const next = new Map(prev);
      next.delete(tagId);
      return next;
    });
  };

  const handleSaveClick = () => {
    if (savingWouldDropTags) return;
    const body = {
      name: currentReferenceData.name,
      documentSuperId: documentId,
      fileRevisionId: maybeFile?.revisionId,
      fileSuperId: maybeFile?.superId,
      selection: {
        ...maybeSelection,
        sheet: maybeSheet,
      },
      selectionContent: workingContent,
      reattachedTagIds: [...reattached.keys()],
      superId: currentReferenceData.superId,
      revisionId: uuidv4(),
    };

    updateReferenceData(
      {
        document: pDocument,
        referenceData: body,
      },
      {
        onSuccess: onSaveSuccess,
      }
    );
  };

  return wrapper(
    t('reference-data:update-values.modal.title'),
    [selectedData, sheetData],
    () => (
      <>
        {priorHasTags && !taggerWarning && (
          <div className="compare-match-mode">
            <span>{t(mode === 'position' ? 'reference-data:review-tags.mode-position' : 'reference-data:review-tags.mode-label')}</span>
            <button type="button" className="button clear" onClick={switchMode}>
              {t(mode === 'position' ? 'reference-data:review-tags.use-label' : 'reference-data:review-tags.use-position')}
            </button>
          </div>
        )}
        {!taggerWarning && <ReviewVerdict results={match?.results} summary={match?.summary} reattached={reattached} dropped={dropped} mode={mode} />}
        {taggerWarning && match && (
          <p className="compare-range-notice compare-top-notice" data-testid="compare-tagger-summary">
            <span>
              {[
                movedCount > 0 && t('reference-data:review-tags.tagger-moved', { count: movedCount }),
                partialCount > 0 && t('reference-data:review-tags.tagger-partial', { count: partialCount }),
                lostCount > 0 && t('reference-data:review-tags.tagger-lost', { count: lostCount }),
              ]
                .filter(Boolean)
                .join(' ')}
            </span>
          </p>
        )}
        {sizeNotice && (
          <p className="compare-range-notice compare-top-notice" data-testid="compare-size-notice">
            <span>
              {t('reference-data:review-tags.size-notice', sizeNotice)}
              {positionView && (
                <>
                  {' '}
                  <Trans t={t} i18nKey="reference-data:review-tags.size-notice-tags" components={{ matcher: matcherLink }} />
                </>
              )}
            </span>
          </p>
        )}
        {rangeNotice && (
          <p className="compare-range-notice compare-top-notice">
            <span>{t('reference-data:review-tags.range-notice', rangeNotice)}</span>
          </p>
        )}
        {positionView && styleIssues.length > 0 && (
          <p className="compare-range-notice compare-top-notice" data-testid="compare-style-short">
            <span>
              <Trans t={t} i18nKey="reference-data:compare.style-issues.short" count={styleIssues.length} components={{ matcher: matcherLink }} />
            </span>
          </p>
        )}
        {!positionView && styleIssues.length > 0 && (
          <div className="compare-range-notice compare-style-notice">
            <span>{t('reference-data:compare.style-issues.title', { count: styleIssues.length })}</span>
            <ul>
              {(showAllStyleIssues ? styleIssues : styleIssues.slice(0, MAX_STYLE_ISSUES)).map((issue) => (
                <li key={`${issue.row}-${issue.col}`}>
                  {t(issue.lost ? 'reference-data:compare.style-issues.lost' : 'reference-data:compare.style-issues.shifted', {
                    row: issue.label ? `"${issue.label}"` : issue.row + 1,
                    col: issue.header ? `"${issue.header}"` : issue.col + 1,
                    styles: issue.styles.join(', '),
                  })}
                </li>
              ))}
            </ul>
            {styleIssues.length > MAX_STYLE_ISSUES && (
              <button type="button" className="button clear text-notransform" onClick={() => setShowAllStyleIssues((shown) => !shown)}>
                {showAllStyleIssues
                  ? t('reference-data:compare.style-issues.less')
                  : t('reference-data:compare.style-issues.more', { count: styleIssues.length - MAX_STYLE_ISSUES })}
              </button>
            )}
          </div>
        )}
        <div className="d-flex gap-r-2">
          <div>
            <h3>{t('reference-data:current-file')}</h3>
            {currentReferenceData.selectionContent ? (
              <>
                <CurrentFileBlob file={currentFile}></CurrentFileBlob>
                <div className="compare-grid">
                  <StyledDataSheet
                    data={displayOld}
                    readOnly={true}
                    highlightedCells={highlightOld}
                    taggedCells={taggedOld}
                    withTags={showTagMarkers}
                    highlightedOption="carry"
                    resolveTagColors={resolveTagColors}
                    tagDataMap={tagData}
                  />
                </div>
              </>
            ) : (
              <p className="text-nowrap">{t('reference-data:no-file-linked')}</p>
            )}
          </div>
          <div>
            <h3>{t('reference-data:new-file')}</h3>
            <CurrentFileBlob file={maybeFile}>
              <div>
                <div className="d-flex gap-r-1 justify-content-end">
                  <div onClick={onNewFileClick}>{t('reference-data:change-file')}</div>
                  <div onClick={onNewSelectionClick}>{t('reference-data:change-selection')}</div>
                </div>
                <div className="text-end font-weight-light text-notransform text-muted">{t('reference-data:compare.style-change-notice')}</div>
              </div>
            </CurrentFileBlob>
            <div className="compare-grid">
              <StyledDataSheet
                data={displayNew}
                readOnly={true}
                highlightedCells={highlightNew}
                taggedCells={taggedNew}
                withTags={showTagMarkers}
                highlightedOption="carry"
                resolveTagColors={resolveTagColors}
                tagDataMap={tagData}
                onSelect={handleNewGridSelect}
              />
            </div>
            {!positionView && !comparable && bothHaveRows && <p className="compare-note">{t('reference-data:review-tags.not-comparable')}</p>}
            {savingWouldDropTags && (
              <p className="compare-note compare-note--blocking">
                {t(matchFailed ? 'reference-data:review-tags.match-failed' : 'reference-data:review-tags.match-pending')}
              </p>
            )}
          </div>
        </div>
        {!taggerWarning && (
          <ReviewTags
            results={match?.results}
            pendingTagId={pendingTagId}
            dropped={dropped}
            reattached={reattached}
            onStartReattach={setPendingTagId}
            onCancelReattach={() => setPendingTagId(null)}
            onDrop={handleDrop}
            onUndo={handleUndo}
            conceptLabels={conceptLabels}
          />
        )}
      </>
    ),
    () => (
      <div className="modal-btn-group">
        <button className="button clear" onClick={onAbortClick}>
          {t('common:cancel')}
        </button>
        <button className="button primary" onClick={handleSaveClick} disabled={savingWouldDropTags}>
          {t('common:save')}
        </button>
      </div>
    ),
    notice
  );
}
